import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  ExternalLink,
  Eye,
  Filter,
  Mail,
  RefreshCw,
  Search,
  Send,
  X,
  XCircle,
} from "lucide-react";
import { listCommunications } from "@/lib/recruitmentApi";

const TYPE_LABELS = {
  refusal: "Refus Candidat",
  invitation: "Invitation CV Candidat",
  client_presentation: "Présentation Client",
};

const TYPE_COLORS = {
  refusal: "bg-rose-500/10 text-rose-300 border-rose-500/20",
  invitation: "bg-sky-500/10 text-sky-300 border-sky-500/20",
  client_presentation: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
};

const STATUS_COLORS = {
  sent: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  ready: "bg-blue-500/10 text-blue-300 border-blue-500/20",
  draft: "bg-slate-500/10 text-slate-300 border-slate-500/20",
  failed: "bg-rose-500/10 text-rose-300 border-rose-500/20",
};

export default function CommunicationsListPage() {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedComm, setSelectedComm] = useState(null);

  const fetchCommunications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCommunications();
      setCommunications(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec de chargement des communications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchCommunications();
  }, [fetchCommunications]);

  const filtered = useMemo(() => {
    return communications.filter((c) => {
      if (typeFilter !== "all" && c.type !== typeFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const subjectMatch = (c.subject || "").toLowerCase().includes(q);
        const recipientMatch = (c.recipients || []).some((r) =>
          String(r).toLowerCase().includes(q),
        );
        return subjectMatch || recipientMatch;
      }
      return true;
    });
  }, [communications, typeFilter, search]);

  return (
    <div className="space-y-6 pb-16" data-testid="communications-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 px-3 py-0.5 text-xs font-semibold text-blue-300 border border-blue-500/20 mb-2">
            <Mail className="h-3.5 w-3.5" />
            Traçabilité & Envois Réels
          </div>
          <h1 className="text-2xl font-heading font-extrabold text-white tracking-tight">
            Communications & Présentations
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Historique complet des emails envoyés : refus candidats, présentations clients et invitations.
          </p>
        </div>

        <button
          onClick={fetchCommunications}
          className="inline-flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-3.5 py-2 text-xs font-semibold text-slate-200 transition-colors self-start sm:self-auto"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Actualiser
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par objet, destinataire..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/80 border border-white/10 text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            data-testid="communications-search"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
          {[
            { id: "all", label: "Toutes" },
            { id: "refusal", label: "Refus" },
            { id: "client_presentation", label: "Présentations client" },
            { id: "invitation", label: "Invitations CV" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setTypeFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                typeFilter === tab.id
                  ? "bg-blue-600 text-white font-semibold"
                  : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 text-rose-300 text-xs">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 glass rounded-2xl border border-white/10 text-slate-500">
          <Mail className="h-10 w-10 mx-auto text-slate-600 mb-3" />
          <p className="text-base font-semibold text-slate-300">Aucune communication enregistrée</p>
          <p className="text-xs text-slate-500 mt-1">
            Les emails de refus ou présentations clients apparaîtront ici dès leur génération.
          </p>
        </div>
      )}

      {/* Communications Cards */}
      {!loading && filtered.length > 0 && (
        <div className="grid gap-3.5">
          {filtered.map((comm) => (
            <div
              key={comm._id}
              className="glass rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
              data-testid={`communication-card-${comm._id}`}
            >
              <div className="space-y-1.5 min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                      TYPE_COLORS[comm.type] || "bg-white/5 text-slate-300 border-white/10"
                    }`}
                  >
                    {TYPE_LABELS[comm.type] || comm.type}
                  </span>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                      STATUS_COLORS[comm.status] || "bg-white/5 text-slate-300 border-white/10"
                    }`}
                  >
                    {comm.status === "sent" ? "Envoyé avec succès" : comm.status}
                  </span>
                  <span className="font-semibold text-white text-sm truncate">
                    {comm.subject}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                  <span>
                    Destinataire(s) :{" "}
                    <span className="text-slate-200 font-medium">
                      {(comm.recipients || []).join(", ") || "Non spécifié"}
                    </span>
                  </span>
                  {comm.created_at && (
                    <span className="flex items-center gap-1 text-slate-500">
                      <Clock className="h-3.5 w-3.5" />
                      {new Date(comm.created_at).toLocaleString("fr-FR")}
                    </span>
                  )}
                  {comm.sent_by && (
                    <span className="text-slate-500">
                      Par : <span className="text-slate-300">{comm.sent_by}</span>
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setSelectedComm(comm)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 rounded-xl text-xs font-semibold transition-colors"
                  data-testid={`preview-comm-btn-${comm._id}`}
                >
                  <Eye className="h-3.5 w-3.5" />
                  Aperçu
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Message Preview Modal */}
      {selectedComm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#101426] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div>
                <span className="text-xs font-mono text-blue-400 uppercase">
                  {TYPE_LABELS[selectedComm.type] || selectedComm.type}
                </span>
                <h3 className="font-bold text-white text-base mt-0.5">{selectedComm.subject}</h3>
              </div>
              <button
                onClick={() => setSelectedComm(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="text-xs text-slate-400 space-y-1">
              <div>
                <span className="text-slate-500">À : </span>
                {(selectedComm.recipients || []).join(", ")}
              </div>
              {selectedComm.sent_at && (
                <div>
                  <span className="text-slate-500">Date d&apos;envoi : </span>
                  {new Date(selectedComm.sent_at).toLocaleString("fr-FR")}
                </div>
              )}
            </div>

            <div className="p-4 rounded-xl bg-slate-900 border border-white/10 text-xs text-slate-200 whitespace-pre-line leading-relaxed max-h-96 overflow-y-auto">
              {selectedComm.body_text || selectedComm.content || "Aucun contenu textuel."}
            </div>

            <div className="flex justify-end pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setSelectedComm(null)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
